import {
  Component,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Observable, map } from 'rxjs';
import { NegotiationDetailsService } from 'src/app/services/negotiation-details.service';
import { Counter, NegotiationDetails } from '../negotiations.model';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { UserDetails } from '../../login/user.model';
import { NegotiationActionsService } from 'src/app/services/negotiation-actions.service';
import { CommonModule, Location } from '@angular/common';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { NegotiationDetailsDialogComponent } from '../../custom/negotiation-details-dialog/negotiation-details-dialog.component';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarModule,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { SocketService } from 'src/app/services/socket.service';
import { LotsService } from 'src/app/services/lots.service';
import {
  DialogType,
  LotActionDialogTemplateComponent,
} from '../../bids-and-offers/desktop-view/negotiation-details-card/lot-action-dialog-template/lot-action-dialog-template.component';
import { CreateNegotiationCounterService } from 'src/app/services/create-negotiation-counter.service';
import { Store } from '@ngrx/store';
import { LotState } from 'src/app/store/lots-library/lots.reducer';
import { TableDataState } from 'src/app/store/table-data/table-data.reducer';
import { PriceType, PriceUnion } from '../../bids-and-offers/lots.model';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { LogEventsEnum } from 'src/app/firebase.model';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PriceTypePipe } from 'src/app/pipes/price-type.pipe';
import { LoadingSpinnerComponent } from '../../custom/loading-spinner/loading-spinner.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatRippleModule, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';

export interface DialogData {
  dialogText: string;
}

@Component({
  selector: 'app-negotiations-details',
  templateUrl: './negotiations-details.component.html',
  styleUrls: ['./negotiations-details.component.css'],
  standalone: true,
  imports: [
    LoadingSpinnerComponent,
    CommonModule,
    RouterModule,
    FontAwesomeModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatRadioModule,
    MatDialogModule,
    MatSnackBarModule,
    MatRippleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSnackBarModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatDividerModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatBottomSheetModule,
    MatMenuModule,
    MatBadgeModule,
    PriceTypePipe,
  ],
})
export class NegotiationsDetailsComponent implements OnInit, OnDestroy {
  private analytics = inject(Analytics);
  negotiation$: Observable<NegotiationDetails>;
  counters$: Observable<Counter>;
  counterPriceType = signal<PriceType>(null);
  lotId: string;
  negotiationId: string;
  activeCounter: Counter;
  isSameYear: boolean;
  userDetails: UserDetails;
  isLoading: boolean;
  dialogConfig = new MatDialogConfig();

  horizontalPosition: MatSnackBarHorizontalPosition = 'center';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';

  constructor(
    private negotiationsDetailsService: NegotiationDetailsService,
    private negotiationsActionsService: NegotiationActionsService,
    private createNegotiationCounterService: CreateNegotiationCounterService,
    private socketService: SocketService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private injector: Injector,
    private router: Router,
    private userService: UserService,
    private lotsService: LotsService,
    private location: Location,
    private snackBar: MatSnackBar,
    private store: Store<{ activeLot: LotState; lotTableData: TableDataState }>
  ) {
    // this.dialogConfig.hasBackdrop = true;
    this.dialogConfig.disableClose = true; //  prevent closing dialog when backdrop clicked
  }

  ngOnInit(): void {
    this.counters$ = this.socketService.getSocketCounterObservable();
    this.userDetails = this.userService.getUserDetails();

    this.negotiationId = this.route.snapshot.paramMap.get('id');
    const negotiationQueryRef = this.negotiationsDetailsService.watch(
      {
        id: this.negotiationId,
      },
      {
        fetchPolicy: 'cache-and-network',
      }
    );
    this.negotiationsDetailsService.setNegotiationQueryRef(negotiationQueryRef);
    this.negotiation$ = negotiationQueryRef.valueChanges.pipe(
      map((result) => {
        logEvent(this.analytics, LogEventsEnum.negotiations_detail_view);
        if (result?.data?.negotiation?.counters.length > 0) {
          this.activeCounter = result?.data?.negotiation?.counters[0];
          this.counterPriceType.set(
            this.lotsService.isCashPrice(
              result.data.negotiation.price_value_orig as PriceUnion
            )
              ? PriceType.Cash
              : PriceType.Basis
          );
        }
        this.negotiationId = result.data.negotiation._id;
        this.lotId = result.data.negotiation.lot_id;
        this.isSameYear = this.checkSameYear(this.activeCounter);
        this.negotiationsDetailsService.setActiveNegotiationDetails(
          result?.data?.negotiation
        );
        return result?.data?.negotiation;
      })
    );

    this.counters$
      .pipe(
        map((res) => {
          // this.negotiationsDetailsService.watch({id: this.negotiationId}, {fetchPolicy: 'cache-and-network'});
        })
      )
      .subscribe((next) => {
        this.negotiation$ = this.negotiationsDetailsService
          .watch(
            {
              id: this.negotiationId,
            },
            {
              fetchPolicy: 'cache-and-network',
            }
          )
          .valueChanges.pipe(
            map((result) => {
              if (result?.data?.negotiation?.counters.length > 0) {
                this.activeCounter = result?.data?.negotiation?.counters[0];
              }
              this.negotiationId = result.data.negotiation._id;
              this.lotId = result.data.negotiation.lot_id;
              this.isSameYear = this.checkSameYear(this.activeCounter);
              this.negotiationsDetailsService.setActiveNegotiationDetails(
                result?.data?.negotiation
              );
              return result?.data?.negotiation;
            })
          );
      });
  }

  checkSameYear(data: any): boolean {
    return (
      new Date(data.ship_begin).getFullYear() ===
      new Date(data.ship_end).getFullYear()
    );
  }

  counter() {
    logEvent(this.analytics, LogEventsEnum.negotiations_detail_counter_tap);
    //open dialog counter
    const counterDialogRef = this.dialog.open(
      LotActionDialogTemplateComponent,
      {
        injector: this.injector,
        data: {
          form: {
            value: {
              ...this.activeCounter,
              ...(this.counterPriceType() === PriceType.Cash
                ? {
                    cash_price: {
                      amount: this.activeCounter.price_value['amount'],
                    },
                    basis_price: null,
                  }
                : {
                    basis_price: { ...this.activeCounter.price_value },
                    cash_price: null,
                  }),
              price_type: this.counterPriceType(),
            },
          },
          lotId: this.lotId,
          dialogTitle: 'Counter',
          dialogType: DialogType.Counter,
        },
      }
    );

    counterDialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result.confirm) {
          let values = { ...result.data };
          values = this.lotsService.removeProperty(
            values,
            this.counterPriceType() === PriceType.Basis
              ? 'cash_price'
              : 'basis_price'
          );
          this.createNegotiationCounterService
            .mutate({
              id: this.negotiationId, //  negotiation id
              input: { ...values },
            })
            .subscribe({
              next: (response) => {
                // console.log(response);
                this.snackBar.open(`Created counter`, 'close', {
                  duration: 3500,
                  panelClass: ['blue-snackbar'],
                });
                logEvent(
                  this.analytics,
                  LogEventsEnum.negotiations_form_submit_tap
                );
                this.negotiationsDetailsService.refresh();
                // make material snackbar confirmation
              },
              error: (response) => {
                this.snackBar.open(`Error Creating Counter`, 'close', {
                  duration: 3500,
                  panelClass: ['blue-snackbar'],
                });
              },
            });
        }
      },
    });
  }

  update() {
    //open dialog counter
    const counterDialogRef = this.dialog.open(
      LotActionDialogTemplateComponent,
      {
        injector: this.injector,
        data: {
          form: {
            value: {
              ...this.activeCounter,
              price_type: this.counterPriceType(),
              ...(this.counterPriceType() === PriceType.Cash
                ? {
                    cash_price: {
                      amount: this.activeCounter.price_value['amount'],
                    },
                    basis_price: null,
                  }
                : {
                    basis_price: { ...this.activeCounter.price_value },
                    cash_price: null,
                  }),
            },
          },
          lotId: this.lotId,
          dialogTitle: 'Counter',
          dialogType: DialogType.Counter,
        },
      }
    );

    counterDialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result.confirm) {
          let values = {
            ...result.data,
          };
          // excludes unnecessary field depending on PriceType
          values = this.lotsService.removeProperty(
            values,
            values.price_type === PriceType.Basis ? 'cash_price' : 'basis_price'
          );

          this.negotiationsActionsService
            .updateNegotiation(this.negotiationId, values)
            .subscribe({
              next: (res) => {
                this.snackBar.open(`Updated Negotiation`, 'close', {
                  duration: 4500,
                  panelClass: ['blue-snackbar'],
                });
                this.negotiationsDetailsService.refresh();
              },
              error: (err) => {
                this.snackBar.open(`Error Updating Counter`, 'close', {
                  duration: 4500,
                  panelClass: ['blue-snackbar'],
                });
              },
            });
        }
      },
    });
  }

  accept() {
    this.dialogConfig.data = {
      dialogText: 'Are you sure you want to accept?',
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log("contract dialog close result", result);
        if (result.confirm) {
          logEvent(
            this.analytics,
            LogEventsEnum.negotiations_detail_accept_tap
          );
          this.negotiationsActionsService
            .createContract({
              lot_id: this.lotId,
              negotiation_id: this.negotiationId,
            })
            .subscribe({
              next: (result) => {
                this.snackBar.open('Negotiation accepted', null, {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 3000,
                  panelClass: ['blue-snackbar'],
                });
                this.router.navigate(['../']);
              },
              error: (err) => {
                console.log('contract creation error', err);
                this.snackBar.open('Error accepting negotiation', null, {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 3000,
                });
              },
            });
        }
      },
    });
  }

  decline(id: string) {
    this.dialogConfig.data = {
      dialogText: 'Are you sure you want to decline the Negotiation?',
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log('decline dialog action', result);
        // yes or confirm continue
        if (result.confirm) {
          // console.log("dialog confirm revoke; Sending Revoke query");
          this.negotiationsActionsService
            .updateNegotiation(id, { status: 'Declined' })
            .subscribe({
              next: (result) => {
                // console.log("revoke query result:",result.data);
                this.snackBar.open('Negotiation declined', 'close', {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  panelClass: ['blue-snackbar'],
                });
                logEvent(
                  this.analytics,
                  LogEventsEnum.negotiations_detail_decline_tap
                );
                this.location.back();
              },
              error: (err) => {
                console.log('error revoking', err);
                this.snackBar.open('Negotiation revoked', null, {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 4000,
                });
              },
            });
        }
      },
    });
  }

  revoke(id: string) {
    logEvent(this.analytics, LogEventsEnum.negotiations_detail_revoke_tap);
    this.dialogConfig.data = {
      dialogText: 'Are you sure you want to revoke the Negotiation?',
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log('dialog action', result);
        // yes or confirm continue
        if (result.confirm) {
          // console.log("dialog confirm revoke; Sending Revoke query");
          this.negotiationsActionsService.revoke(id.toString()).subscribe({
            next: (result) => {
              // console.log("revoke query result:",result.data);
              this.snackBar.open('Negotiation revoked', 'close', {
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                duration: 4000,
                panelClass: ['blue-snackbar'],
              });
              this.location.back();
            },
            error: (err) => {
              console.log('error revoking', err);
              this.snackBar.open('Negotiation revoked', null, {
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                duration: 4000,
                panelClass: ['blue-snackbar'],
              });
            },
          });
        }
      },
    });
  }

  ngOnDestroy(): void {}
}
